import { useMemo, useState } from "react";
import {
  getCanonicalEntityIds,
  getItemProfileTypeKey,
  isSameEntity,
} from "../../utils/messengerLeftSection";

const useLeftSection = ({
  searchTerm,
  users,
  searchedData,
  safeRender,
  connectedShops,
  connectedProfilesOnly,
  selectedConnectedTabId = "",
  userShops,
  activeTabType,
  selectedProfile,
  onSelectShop,
  handleUserClick,
  handleSearchDataClick,
}) => {
  const filteredUsers = useMemo(() => {
    if (!searchTerm) return users;

    return (
      users?.filter((user) => {
        const nameCandidates = [
          user?.otherParticipantData?.fullname,
          user?.otherParticipantData?.shopname,
          user?.otherParticipantData?.displayName,
          user?.otherParticipantData?.name,
          user?.shopname,
          user?.name,
        ];
        const validName = nameCandidates.find(
          (n) =>
            n &&
            typeof n === "string" &&
            n.trim() !== "" &&
            n.trim().toUpperCase() !== "N/A",
        );
        const userName = safeRender(validName);
        return userName.toLowerCase().includes(searchTerm.toLowerCase());
      }) || []
    );
  }, [users, searchTerm, safeRender]);

  const profileFilteredUsers = useMemo(
    () => filteredUsers || [],
    [filteredUsers],
  );
  const profileRelatedConnections = useMemo(
    () => connectedShops || [],
    [connectedShops],
  );
  const profileSearchedData = useMemo(() => searchedData || [], [searchedData]);

  const displayUsers = useMemo(() => {
    const seen = new Set();
    const unique = [];

    const addUnique = (item) => {
      if (!item) return;
      const keys = getCanonicalEntityIds(item);
      if (keys.some((key) => seen.has(key))) return;
      keys.forEach((key) => seen.add(key));
      unique.push(item);
    };

    const isShopEntity = (item) => {
      if (!item) return false;
      const contact = item.otherParticipantData || item;
      if (
        contact.position === "Shop" ||
        item.otherposition === "Shop" ||
        item.position === "Shop"
      ) {
        return true;
      }
      const hasShopId = Boolean(
        contact.shopid ||
        contact.shopId ||
        contact.serviceid ||
        contact.companyid ||
        item.othershopid ||
        item.shopid ||
        item.shopId
      );
      if (hasShopId) return true;

      const bt = String(
        contact.businessType ||
        contact.linkBusinessType ||
        contact.type ||
        contact.category ||
        item.otherbusinessType ||
        item.businessType ||
        ""
      )
        .toLowerCase()
        .replace(/[\s_-]+/g, "");

      if (!bt || bt === "generalpublic" || bt === "user") {
        return false;
      }
      return true;
    };

    const matchesCategory = (item, targetKey) => {
      if (!item || !targetKey) return false;
      const normalizedTarget = targetKey
        .toLowerCase()
        .replace(/^type:/, "")
        .replace(/[\s_-]+/g, "");
      if (normalizedTarget === "user" || normalizedTarget === "generalpublic") {
        return !isShopEntity(item);
      }

      // Check tab ID fields directly if present
      const tabId = String(
        item.connectedProfileTabId ||
        item.tabId ||
        item.connectedProfileTab ||
        "",
      )
        .toLowerCase()
        .replace(/^type:/, "")
        .replace(/[\s_-]+/g, "");
      if (tabId && (tabId === normalizedTarget || tabId.includes(normalizedTarget) || normalizedTarget.includes(tabId))) {
        return true;
      }

      const itemTypeKey = String(getItemProfileTypeKey(item) || "")
        .toLowerCase()
        .replace(/^type:/, "")
        .replace(/[\s_-]+/g, "");

      if (itemTypeKey && (itemTypeKey === normalizedTarget || itemTypeKey.includes(normalizedTarget) || normalizedTarget.includes(itemTypeKey))) {
        return true;
      }

      const contact = item.otherParticipantData || item;
      const rawBt = String(
        contact.businessType ||
        contact.linkBusinessType ||
        contact.otherbusinessType ||
        contact.type ||
        contact.category ||
        item.otherbusinessType ||
        item.businessType ||
        contact.tabLabel ||
        item.tabLabel ||
        ""
      )
        .toLowerCase()
        .replace(/^type:/, "")
        .replace(/[\s_-]+/g, "");

      if (rawBt && (rawBt.includes(normalizedTarget) || normalizedTarget.includes(rawBt))) {
        return true;
      }

      if (
        normalizedTarget.includes("whole") &&
        (rawBt.includes("whole") || itemTypeKey.includes("whole"))
      ) {
        return true;
      }
      if (
        normalizedTarget.includes("retail") &&
        (rawBt.includes("retail") || itemTypeKey.includes("retail"))
      ) {
        return true;
      }
      if (
        (normalizedTarget.includes("food") || normalizedTarget.includes("restaurant")) &&
        (rawBt.includes("food") || rawBt.includes("restaurant") || itemTypeKey.includes("food") || itemTypeKey.includes("restaurant"))
      ) {
        return true;
      }
      if (
        normalizedTarget.includes("service") &&
        (rawBt.includes("service") || itemTypeKey.includes("service"))
      ) {
        return true;
      }
      if (
        normalizedTarget.includes("manufacturer") &&
        (rawBt.includes("manufacturer") || itemTypeKey.includes("manufacturer"))
      ) {
        return true;
      }
      if (
        normalizedTarget.includes("rawmaterial") &&
        (rawBt.includes("rawmaterial") || itemTypeKey.includes("rawmaterial"))
      ) {
        return true;
      }
      if (
        normalizedTarget.includes("gem") &&
        (rawBt.includes("gem") || itemTypeKey.includes("gem"))
      ) {
        return true;
      }
      if (
        (normalizedTarget.includes("vehicle") || normalizedTarget.includes("usedvehicle")) &&
        (rawBt.includes("vehicle") || itemTypeKey.includes("vehicle"))
      ) {
        return true;
      }
      if (
        normalizedTarget.includes("used") &&
        (rawBt.includes("used") || itemTypeKey.includes("used"))
      ) {
        return true;
      }
      if (
        normalizedTarget.includes("farm") &&
        (rawBt.includes("farm") || itemTypeKey.includes("farm"))
      ) {
        return true;
      }
      if (
        normalizedTarget.includes("recycle") &&
        (rawBt.includes("recycle") || itemTypeKey.includes("recycle"))
      ) {
        return true;
      }
      if (
        normalizedTarget.includes("transport") &&
        (rawBt.includes("transport") || itemTypeKey.includes("transport"))
      ) {
        return true;
      }
      if (
        normalizedTarget.includes("property") &&
        (rawBt.includes("property") || itemTypeKey.includes("property"))
      ) {
        return true;
      }
      if (
        normalizedTarget.includes("job") &&
        (rawBt.includes("job") || itemTypeKey.includes("job"))
      ) {
        return true;
      }
      if (
        normalizedTarget.includes("market") &&
        (rawBt.includes("market") || itemTypeKey.includes("market"))
      ) {
        return true;
      }

      return false;
    };

    if (searchTerm) {
      if (profileFilteredUsers.length > 0) {
        profileFilteredUsers.forEach(addUnique);
        return unique;
      }
      profileSearchedData.forEach(addUnique);
      return unique;
    }

    const rawTabKey = String(
      selectedConnectedTabId ||
      activeTabType ||
      (selectedProfile?.isGeneralPublic ? "user" : "") ||
      "user",
    )
      .replace(/^type:/, "")
      .toLowerCase()
      .trim();
    // 1. Add chats that match this category or match any connection in this category
    profileFilteredUsers.forEach((chat) => {
      const matchesConn = profileRelatedConnections.some(
        (conn) => isSameEntity(conn, chat),
      );
      if (matchesConn || matchesCategory(chat, rawTabKey)) {
        addUnique(chat);
      }
    });

    // 2. Add all connections belonging to this selected tab category
    profileRelatedConnections.forEach(addUnique);

    return unique;
  }, [
    profileFilteredUsers,
    profileSearchedData,
    profileRelatedConnections,
    selectedConnectedTabId,
    connectedProfilesOnly,
    activeTabType,
    selectedProfile,
    searchTerm,
  ]);

  const [selectedConnectedProfileIds, setSelectedConnectedProfileIds] =
    useState([]);

  const selectShopProfile = (shop) => {
    if (shop && onSelectShop && !isSameEntity(selectedProfile, shop)) {
      onSelectShop(shop);
    }
  };

  const handleItemClick = (item) => {
    const ownShop = userShops.find((shop) => isSameEntity(shop, item));
    selectShopProfile(ownShop);
    setSelectedConnectedProfileIds([]);
  };

  const handleListItemClick = (item) => {
    handleOpenChatForProfile(item, false, true);
  };

  const handleOpenChatForProfile = (
    item,
    selectOwnProfile = true,
    fromConnectedProfiles = false,
  ) => {
    if (fromConnectedProfiles) {
      setSelectedConnectedProfileIds(getCanonicalEntityIds(item));
    }

    const ownShop = userShops.find((shop) => isSameEntity(shop, item));
    if (selectOwnProfile) {
      selectShopProfile(ownShop);
    }

    if (item?.otherParticipantData) {
      handleUserClick(item);
      return;
    }

    const targetShopId = String(item?.shopid || item?.id || item?._id || "");
    const existingChat = users?.find((user) => {
      const otherId = String(
        user?.otherParticipantData?._id ||
          user?.otherParticipantData?.shopid ||
          user?.otherParticipantData?.userid ||
          "",
      );
      return (
        (otherId && otherId === targetShopId) ||
        (targetShopId && String(user?._id) === targetShopId)
      );
    });

    if (existingChat) {
      handleUserClick(existingChat);
      return;
    }

    const targetPayload = {
      ...item,
      shopid: targetShopId,
      shopname:
        item?.shopname ||
        item?.displayName ||
        item?.fullname ||
        item?.name ||
        "Shop",
      position:
        item?.position ||
        (item?.businessType === "generalPublic" ? "User" : "Shop"),
      profilePic:
        item?.profilePic ||
        item?.imageUrl ||
        item?.image ||
        (Array.isArray(item?.images) ? item.images[0] : null) ||
        null,
      businessType: item?.businessType || "shop",
    };
    handleSearchDataClick(targetPayload);
  };

  return {
    displayUsers,
    filteredUsers: profileFilteredUsers,
    handleListItemClick,
    handleOpenChatForProfile,
    selectedConnectedProfileIds,
  };
};

export default useLeftSection;
